import React, { useState, useEffect } from 'react'
import { Form } from 'react-bootstrap'
import { usePost, notify } from '../../../../components/component-items/helpers'
import GenericModal from '../../../../components/component-items/modal'
import GlobalStyles from '../../../../components/component-items/styles'
import { usePermissions } from '../../../../hooks/usePermissions'

import type { WarehouseUserRoleAssignment, RoleListItem, WarehouseOption } from '../../../../types/rbac'

interface AssignRoleModalProps {
  show: boolean
  onHide: () => void
  onSaved: () => void
  userId: number
  existingAssignment: WarehouseUserRoleAssignment | null
  availableRoles: RoleListItem[]
  warehouses: WarehouseOption[]
  currentWarehouseId: number | null
  csrf: string
}

export const AssignRoleModal = ({
  show,
  onHide,
  onSaved,
  userId,
  existingAssignment,
  availableRoles,
  warehouses,
  currentWarehouseId,
  csrf,
}: AssignRoleModalProps) => {
  const [selectedRoleId, setSelectedRoleId] = useState<number | ''>('')
  const [scopeType, setScopeType] = useState<'global' | 'warehouse'>('warehouse')
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | ''>('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Only global settings:admin users can assign roles at global scope
  const { hasGlobalAccess } = usePermissions()
  const canAssignGlobalScope = hasGlobalAccess('settings', 'admin')

  // Reset form when modal opens/closes or when editing different assignment
  useEffect(() => {
    if (show) {
      if (existingAssignment) {
        setSelectedRoleId(existingAssignment.role)
        // If user cannot assign global scope, force warehouse even if existing assignment is global
        setScopeType(
          existingAssignment.scope_type === 'global' && !canAssignGlobalScope
            ? 'warehouse'
            : existingAssignment.scope_type
        )
        setSelectedWarehouseId(existingAssignment.warehouse || '')
      } else {
        setSelectedRoleId('')
        setScopeType('warehouse')
        setSelectedWarehouseId(currentWarehouseId || '')
      }
    }
  }, [show, existingAssignment, currentWarehouseId, canAssignGlobalScope])

  const handleSubmit = async () => {
    if (!selectedRoleId) {
      notify({ type: 'error', message: 'Please select a role' })
      return
    }

    if (scopeType === 'warehouse' && !selectedWarehouseId) {
      notify({ type: 'error', message: 'Please select a warehouse' })
      return
    }

    setIsSubmitting(true)

    try {
      const payload = {
        role_id: selectedRoleId,
        warehouse_id: scopeType === 'warehouse' ? selectedWarehouseId : null,
      }

      const resp = await usePost(`/api/rbac/users/${userId}/roles/`, payload, csrf, false, true, true, true, false)

      if (resp?.data) {
        notify({ type: 'success', message: existingAssignment ? 'Role assignment updated' : 'Role assigned' })
        onSaved()
      } else {
        const errorMsg = resp?.error?.message || 'Failed to assign role'
        notify({ type: 'error', message: errorMsg })
      }
    } catch (error) {
      notify({ type: 'error', message: 'Failed to assign role' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedRole = availableRoles.find((r) => r.id === selectedRoleId)

  return (
    <GenericModal
      heading={existingAssignment ? 'Edit Role Assignment' : 'Assign Role'}
      show={show}
      onHide={onHide}
      buttons={
        <>
          <GlobalStyles.Button className="secondary" onClick={onHide} disabled={isSubmitting}>
            Cancel
          </GlobalStyles.Button>
          <GlobalStyles.Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : existingAssignment ? 'Update' : 'Assign'}
          </GlobalStyles.Button>
        </>
      }
    >
      <Form style={{ margin: '1.5em' }}>
        {/* Role Selection */}
        <Form.Group className="required" style={{ marginBottom: '1.5em' }}>
          <Form.Label>Role</Form.Label>
          <Form.Select
            value={selectedRoleId}
            onChange={(e) => setSelectedRoleId(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">Select a role...</option>
            {[...availableRoles]
              .sort((a, b) => b.level - a.level)
              .map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                  {role.is_system ? ' (System)' : ''}
                </option>
              ))}
          </Form.Select>
          {selectedRole && (
            <Form.Text className="text-muted" style={{ display: 'block', marginTop: '0.5em' }}>
              {selectedRole.description}
            </Form.Text>
          )}
        </Form.Group>

        {/* Scope Selection */}
        <Form.Group className="required" style={{ marginBottom: '1.5em' }}>
          <Form.Label>Scope</Form.Label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75em', marginTop: '0.5em' }}>
            <Form.Check
              type="radio"
              id="scope-warehouse"
              name="scope"
              label="Specific Warehouse"
              checked={scopeType === 'warehouse'}
              onChange={() => setScopeType('warehouse')}
            />
            {canAssignGlobalScope && (
              <Form.Check
                type="radio"
                id="scope-global"
                name="scope"
                label="Global (applies to all warehouses)"
                checked={scopeType === 'global'}
                onChange={() => setScopeType('global')}
              />
            )}
          </div>
        </Form.Group>

        {/* Warehouse Selection (conditional) */}
        {scopeType === 'warehouse' && (
          <Form.Group className="required" style={{ marginBottom: '1.5em' }}>
            <Form.Label>Warehouse</Form.Label>
            <Form.Select
              value={selectedWarehouseId}
              onChange={(e) => setSelectedWarehouseId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Select a warehouse...</option>
              {warehouses.map((wh) => (
                <option key={wh.id} value={wh.id}>
                  {wh.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {/* Info box */}
        <div
          style={{
            background: '#e3f2fd',
            padding: '1em',
            borderRadius: '4px',
            fontSize: '0.9em',
            color: '#1565c0',
          }}
        >
          <strong>Note:</strong> If the user already has a role at this scope, it will be replaced with the new role.
        </div>
      </Form>
    </GenericModal>
  )
}

export default AssignRoleModal
